import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Sparkles, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export type PlanGateVariant = "paid" | "ultra";

export interface PlanGateOverlayProps {
  variant: PlanGateVariant;
  children: React.ReactNode;
}

type PaidContextKey =
  | "default"
  | "fiscalStatus"
  | "agenda"
  | "dgiDocuments"
  | "documents";

type UltraContextKey = "default" | "dgiDocuments";

function resolvePaidContext(pathname: string): PaidContextKey {
  if (pathname.includes("/fiscal-status")) return "fiscalStatus";
  if (pathname.includes("/agenda")) return "agenda";
  if (pathname.includes("/dgi-documents")) return "dgiDocuments";
  if (pathname.includes("/documents")) return "documents";
  return "default";
}

function resolveUltraContext(pathname: string): UltraContextKey {
  if (pathname.includes("/dgi-documents")) return "dgiDocuments";
  return "default";
}

const TITLE_ID = "plan-gate-overlay-title";

export function PlanGateOverlay({ variant, children }: PlanGateOverlayProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const baseNs = variant === "paid" ? "settings.plans.gate" : "settings.plans.ultraGate";
  const contextKey =
    variant === "paid" ? resolvePaidContext(pathname) : resolveUltraContext(pathname);
  const contextNs = `${baseNs}.contexts.${contextKey}`;

  const bulletsRaw = t(`${contextNs}.bullets`, { returnObjects: true });
  const bullets: string[] = Array.isArray(bulletsRaw) ? bulletsRaw : [];

  const goToBilling = (): void => {
    void navigate("/settings?tab=billing");
  };

  return (
    <div className="relative min-h-full">
      <div
        className={cn(
          "pointer-events-none select-none",
          "motion-reduce:select-auto"
        )}
        aria-hidden
      >
        {children}
      </div>
      <div
        className={cn(
          // items-START, not items-center. A flex container that centres its
          // child clips overflow at BOTH ends, and the top half becomes
          // unreachable by scrolling — so wherever the gated child is shorter
          // than this panel (the Marcas card, for one) the bullets were cut
          // off above and "Ir a planes" below. `my-auto` on the panel keeps it
          // centred when there IS room.
          "absolute inset-0 z-10 flex items-start justify-center overflow-y-auto p-4",
          "bg-background/30 backdrop-blur-sm",
          "motion-reduce:backdrop-blur-none motion-reduce:bg-background/50"
        )}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={TITLE_ID}
          className="pointer-events-auto my-auto w-full max-w-lg overflow-hidden rounded-xl border border-border bg-card shadow-lg transition-colors duration-200"
        >
          <div className="space-y-5 p-6">
            <div className="flex gap-4">
              <div
                className={cn(
                  "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
                  variant === "paid"
                    ? "bg-primary/10 text-primary"
                    : "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                )}
                aria-hidden
              >
                {variant === "paid" ? (
                  <Sparkles className="h-6 w-6" strokeWidth={2} />
                ) : (
                  <Zap className="h-6 w-6" strokeWidth={2} />
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <h2
                  id={TITLE_ID}
                  className="text-lg font-semibold leading-snug tracking-tight text-foreground"
                >
                  {t(`${contextNs}.title`)}
                </h2>
                <p className="text-sm text-muted-foreground">{t(`${contextNs}.subtitle`)}</p>
              </div>
            </div>

            <Separator />

            <p className="text-sm leading-relaxed text-muted-foreground">
              <Trans
                i18nKey={`${contextNs}.body`}
                components={{
                  bold: <strong className="font-semibold text-foreground" />,
                }}
              />
            </p>

            <ul className="space-y-2.5">
              {bullets.map((line, index) => (
                <li key={`${contextKey}-${index}`} className="flex gap-2.5 text-sm text-muted-foreground">
                  <Check
                    className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                    strokeWidth={2.5}
                    aria-hidden
                  />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <Separator />

            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="text-muted-foreground">{t(`${baseNs}.planRowLabel`)}</span>
              <span className="font-semibold text-foreground">{t(`${baseNs}.planRowValue`)}</span>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-border bg-muted/40 px-6 py-4 dark:bg-muted/25">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Button
                type="button"
                variant="link"
                className="h-auto justify-start px-0 text-primary"
                onClick={goToBilling}
              >
                {t(`${baseNs}.learnMore`)}
              </Button>
              <Button
                type="button"
                className="w-full shrink-0 transition-colors duration-150 sm:w-auto"
                onClick={goToBilling}
              >
                {t(`${baseNs}.cta`)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
